/**
 * SkillDelivery Platform
 *
 * Copyright 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 */

import React, { useState, useCallback } from 'react';
import './GoalCard.css';

const GoalCard = ({
  goal,
  onPriorityChange,
  onStatusChange,
  onProgressChange,
  onDelete,
  insights = []
}) => {
  const [priority, setPriority] = useState(goal.priority);
  const [isDragging, setIsDragging] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const handlePriorityChange = useCallback((e) => {
    setPriority(parseInt(e.target.value));
  }, []);

  const handlePriorityCommit = useCallback(() => {
    if (priority !== goal.priority) {
      onPriorityChange(goal.id, priority);
    }
    setIsDragging(false);
  }, [priority, goal.id, goal.priority, onPriorityChange]);

  const getStatusIcon = (status) => {
    const icons = {
      active: '🎯',
      achieved: '✅',
      paused: '⏸️',
      abandoned: '❌'
    };
    return icons[status] || '🎯';
  };

  const getPriorityColor = (p) => {
    if (p >= 8) return '#22c55e'; // Green - High priority
    if (p >= 5) return '#f59e0b'; // Orange - Medium
    return '#94a3b8'; // Gray - Low
  };

  const getInsightStyle = (severity) => {
    const styles = {
      urgent: { bg: '#fef2f2', border: '#fecaca', icon: '🚨' },
      warning: { bg: '#fffbeb', border: '#fde68a', icon: '⚠️' },
      success: { bg: '#f0fdf4', border: '#bbf7d0', icon: '✅' },
      info: { bg: '#eff6ff', border: '#bfdbfe', icon: '💡' }
    };
    return styles[severity] || styles.info;
  };

  // Filter insights for this specific goal
  const goalInsights = insights.filter(i => i.goal_id === goal.id);

  return (
    <div className={`goal-card ${isDragging ? 'dragging' : ''} status-${goal.status}`}>
      {/* Header */}
      <div className="goal-header">
        <div className="goal-title">
          <span className="goal-status-icon">{getStatusIcon(goal.status)}</span>
          <span className="goal-text">{goal.goal_text}</span>
        </div>
        <div className="goal-header-right">
          <div
            className="goal-priority-badge"
            style={{ backgroundColor: getPriorityColor(priority) }}
          >
            {priority}/10
          </div>
          <button
            className="goal-menu-btn"
            onClick={() => setShowActions(!showActions)}
            aria-label="Goal actions"
          >
            ⋮
          </button>
        </div>
      </div>

      {/* Actions dropdown */}
      {showActions && (
        <div className="goal-actions-dropdown">
          <button onClick={() => { onStatusChange(goal.id, 'active'); setShowActions(false); }}>
            🎯 Set Active
          </button>
          <button onClick={() => { onStatusChange(goal.id, 'achieved'); setShowActions(false); }}>
            ✅ Mark Achieved
          </button>
          <button onClick={() => { onStatusChange(goal.id, 'paused'); setShowActions(false); }}>
            ⏸️ Pause
          </button>
          <button
            onClick={() => { onDelete(goal.id); setShowActions(false); }}
            className="delete-action"
          >
            🗑️ Delete
          </button>
        </div>
      )}

      {/* Priority Slider */}
      <div className="priority-slider-container">
        <div className="priority-labels">
          <span>Low Priority</span>
          <span>High Priority</span>
        </div>
        <div className="slider-wrapper">
          <div
            className="slider-fill"
            style={{
              width: `${(priority / 10) * 100}%`,
              backgroundColor: getPriorityColor(priority)
            }}
          />
          <input
            type="range"
            min="1"
            max="10"
            value={priority}
            onChange={handlePriorityChange}
            onMouseDown={() => setIsDragging(true)}
            onMouseUp={handlePriorityCommit}
            onTouchStart={() => setIsDragging(true)}
            onTouchEnd={handlePriorityCommit}
            onBlur={handlePriorityCommit}
            className="priority-slider"
            aria-label={`Priority: ${priority} out of 10`}
          />
        </div>
        <div className="priority-value-display">
          Priority: <strong>{priority}</strong>
        </div>
      </div>

      {/* Progress & Status Row */}
      <div className="goal-meta-row">
        <div className="goal-status-select">
          <select
            value={goal.status}
            onChange={(e) => onStatusChange(goal.id, e.target.value)}
            aria-label="Goal status"
          >
            <option value="active">🎯 Active</option>
            <option value="paused">⏸️ Paused</option>
            <option value="achieved">✅ Achieved</option>
            <option value="abandoned">❌ Abandoned</option>
          </select>
        </div>

        <div className="goal-progress-section">
          <span className="progress-label">Progress: {goal.progress}%</span>
          <div className="progress-track">
            <div
              className="progress-fill"
              style={{ width: `${goal.progress}%` }}
            />
          </div>
        </div>

        {goal.target_date && (
          <div className="goal-target-date">
            🗓️ {new Date(goal.target_date).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* Goal Description (if exists) */}
      {goal.goal_description && (
        <div className="goal-description">
          {goal.goal_description}
        </div>
      )}

      {/* Insights for this goal */}
      {goalInsights.length > 0 && (
        <div className="goal-insights-section">
          {goalInsights.slice(0, 2).map(insight => {
            const style = getInsightStyle(insight.severity);
            return (
              <div
                key={insight.id}
                className="goal-insight-item"
                style={{ backgroundColor: style.bg, borderColor: style.border }}
              >
                <span className="insight-icon">{style.icon}</span>
                <span className="insight-message">{insight.message}</span>
                {insight.action_url && (
                  <a href={insight.action_url} className="insight-action-link">
                    {insight.action_text || 'View'} →
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GoalCard;
